import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ChatService, ChatMessageResponse, ChatConversationResponse } from '../../services/chat.service';
import { NotificationService } from '../../services/notification.service';
import { AuthService } from '../../services/auth.service';
import { AvatarService } from '../../services/avatar.service';
import { Subject, interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-chat-popup',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './chat-popup.component.html',
  styleUrls: ['./chat-popup.component.css']
})
export class ChatPopupComponent implements OnInit, OnDestroy, OnChanges {
  @Input() isVisible: boolean = false;
  @Input() otherUserId: number = 0;
  @Input() otherUserName: string = '';
  @Input() otherUserProfileImageUrl?: string;
  @Output() closeChat = new EventEmitter<void>();

  messageForm: FormGroup;
  messages: ChatMessageResponse[] = [];
  isLoading = false;
  isSending = false;
  errorMessage = '';
  hasNewMessagesInBackground = false;
  private destroy$ = new Subject<void>();
  private refreshInterval?: any;
  private lastCheckTime = 0;
  
  // Cached default avatar as data URL to avoid repeated requests
  readonly defaultAvatarUrl = this.avatarService.getDefaultAvatarUrl();

  constructor(
    private fb: FormBuilder,
    private chatService: ChatService,
    private notificationService: NotificationService,
    private authService: AuthService,
    private avatarService: AvatarService
  ) {
    this.messageForm = this.fb.group({
      message: ['', [Validators.required, Validators.maxLength(2000)]]
    });
  }

  ngOnInit(): void {
    if (this.isVisible && this.otherUserId > 0) {
      this.loadConversation();
      this.startAutoRefresh();
    }
  }

  ngOnChanges(): void {
    console.log('🔍 ChatPopup ngOnChanges called:', {
      isVisible: this.isVisible,
      otherUserId: this.otherUserId,
      otherUserName: this.otherUserName
    });
    
    // Reload conversation when inputs change
    if (this.isVisible && this.otherUserId > 0) {
      this.loadConversation();
      this.startAutoRefresh();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopAutoRefresh();
  }

  onClose(): void {
    this.stopAutoRefresh();
    this.closeChat.emit();
  }

  onSendMessage(): void {
    if (this.messageForm.valid && !this.isSending) {
      this.isSending = true;
      this.errorMessage = '';

      const messageText = this.messageForm.get('message')?.value?.trim();
      if (!messageText) {
        this.isSending = false;
        return;
      }

      this.chatService.sendMessage({
        receiverId: this.otherUserId,
        message: messageText
      }).pipe(takeUntil(this.destroy$)).subscribe({
        next: (response: any) => {
          this.isSending = false;
          if (response.success) {
            this.messageForm.get('message')?.setValue('');
            // Add the sent message immediately to UI for better UX
            this.addSentMessageToUI(messageText);
            // Then refresh in background to get any server-side updates
            setTimeout(() => this.loadConversation(false), 500);
          } else {
            this.errorMessage = response.message || 'Failed to send message';
          }
        },
        error: (error) => {
          this.isSending = false;
          this.errorMessage = error.error?.message || 'Failed to send message';
        }
      });
    } else {
      this.messageForm.markAllAsTouched();
    }
  }

  onKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.onSendMessage();
    }
  }

  private loadConversation(showLoading: boolean = true): void {
    console.log('🔍 ChatPopup loadConversation called with otherUserId:', this.otherUserId);
    
    if (this.otherUserId <= 0) {
      console.log('🔍 ChatPopup: Invalid otherUserId, returning');
      return;
    }

    if (showLoading) {
      this.isLoading = true;
    }
    this.errorMessage = '';

    this.chatService.getConversation(this.otherUserId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          console.log('🔍 ChatPopup conversation response:', response);
          this.isLoading = false;
          if (response.success) {
            const newMessages = response.data.messages || [];
            const hasNewMessages = this.hasNewMessages(this.messages, newMessages);
            
            this.messages = newMessages;
            console.log('🔍 ChatPopup loaded messages:', this.messages);
            
            // Handle background refresh indicator
            if (!showLoading && hasNewMessages) {
              this.hasNewMessagesInBackground = true;
            }
            
            // Only scroll to bottom if there are new messages or it's the initial load
            if (hasNewMessages || showLoading) {
              this.scrollToBottom();
              this.hasNewMessagesInBackground = false; // Clear indicator when scrolling
            }
            
            // Only mark as read on initial load or when user is actively viewing
            if (showLoading) {
              this.markMessagesAsRead();
            }
          } else {
            this.errorMessage = response.message || 'Failed to load conversation';
            console.error('🔍 ChatPopup failed to load conversation:', response.message);
          }
        },
        error: (error) => {
          console.error('🔍 ChatPopup error loading conversation:', error);
          this.isLoading = false;
          this.errorMessage = error.error?.message || 'Failed to load conversation';
        }
      });
  }

  private markMessagesAsRead(): void {
    this.chatService.markMessagesAsRead({ otherUserId: this.otherUserId })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Messages marked as read - refresh notification count
          this.notificationService.fetchNotificationCount().subscribe({
            next: (count) => {
              this.notificationService.updateNotificationCount(count);
            },
            error: (error) => {
              console.error('Failed to refresh notification count:', error);
            }
          });
        },
        error: (error) => {
          console.error('Failed to mark messages as read:', error);
        }
      });
  }

  private startAutoRefresh(): void {
    // Only start auto-refresh if user is authenticated
    if (!this.authService.isAuthenticated()) {
      console.log('🔍 Cannot start auto-refresh - user not authenticated');
      return;
    }
    
    // Stop any existing interval first
    this.stopAutoRefresh();
    
    // Check for new messages every 7 seconds, only reload if there are new messages
    this.refreshInterval = setInterval(() => {
      this.checkForNewMessagesAndReload();
    }, 7000);
  }

  private checkForNewMessagesAndReload(): void {
    // Check if user is authenticated before making the API call
    if (!this.authService.isAuthenticated()) {
      console.log('🔍 Skipping check - user not authenticated');
      this.stopAutoRefresh();
      return;
    }
    
    const now = Date.now();
    // Avoid checking too frequently (minimum 3 seconds between checks)
    if (now - this.lastCheckTime < 3000) {
      console.log('🔍 Skipping check - too recent');
      return;
    }
    
    this.lastCheckTime = now;
    
    // Use the lightweight checkForNewMessages API first
    this.chatService.checkForNewMessages()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          // Verify user is still authenticated
          if (!this.authService.isAuthenticated()) {
            this.stopAutoRefresh();
            return;
          }
          
          if (response.success && response.data.hasNewMessages) {
            console.log('🔍 New messages detected, reloading conversation');
            this.loadConversation(false); // Only reload if there are new messages
          } else {
            console.log('🔍 No new messages, skipping reload');
          }
        },
        error: (error) => {
          // Stop auto-refresh on authentication errors
          if (error.status === 401 || error.status === 403) {
            console.log('🔍 Authentication error, stopping auto-refresh');
            this.stopAutoRefresh();
            return;
          }
          
          console.error('🔍 Error checking for new messages:', error);
          // Fallback: reload conversation if check fails (only if authenticated)
          if (this.authService.isAuthenticated()) {
            this.loadConversation(false);
          }
        }
      });
  }

  private stopAutoRefresh(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = undefined;
    }
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const chatContainer = document.querySelector('.chat-messages');
      if (chatContainer) {
        chatContainer.scrollTop = chatContainer.scrollHeight;
      }
    }, 100);
  }

  private hasNewMessages(oldMessages: ChatMessageResponse[], newMessages: ChatMessageResponse[]): boolean {
    if (oldMessages.length === 0) return false;
    if (newMessages.length === 0) return false;
    if (newMessages.length !== oldMessages.length) return true;
    
    // Check if the last message is different
    const lastOldMessage = oldMessages[oldMessages.length - 1];
    const lastNewMessage = newMessages[newMessages.length - 1];
    
    return lastOldMessage.id !== lastNewMessage.id || 
           lastOldMessage.message !== lastNewMessage.message ||
           lastOldMessage.sentAt !== lastNewMessage.sentAt;
  }

  private addSentMessageToUI(messageText: string): void {
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) return;

    const tempMessage: ChatMessageResponse = {
      id: Date.now(), // Temporary ID
      senderId: currentUser.id,
      senderName: `${currentUser.firstName} ${currentUser.lastName}`,
      senderProfileImageUrl: undefined, // User interface doesn't have profileImageUrl
      receiverId: this.otherUserId,
      receiverName: this.otherUserName,
      receiverProfileImageUrl: this.otherUserProfileImageUrl,
      message: messageText,
      sentAt: new Date().toISOString(),
      isRead: false,
      isFromCurrentUser: true
    };

    this.messages.push(tempMessage);
    this.scrollToBottom();
  }

  formatMessageTime(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
  }

  onImageError(event: Event): void {
    this.avatarService.handleImageError(event);
  }

  onScrollToBottom(): void {
    this.hasNewMessagesInBackground = false;
  }

  onScroll(event: Event): void {
    const element = event.target as HTMLElement;
    const isAtBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 10;
    
    if (isAtBottom) {
      this.hasNewMessagesInBackground = false;
    }
  }
}
